import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Whether the app turns with the phone.
 *
 * Portrait is the default and was previously enforced in the Android wrapper,
 * where nothing in the app could override it. It is enforced here instead, so
 * that "follow the system" is a choice the traveller can actually make - the
 * Android side now defers, which is the only way a toggle can mean anything.
 *
 * Per device, not per account: which way a phone may turn is a property of the
 * phone in your hand, not of who is signed in on it.
 */
export type OrientationMode = "portrait" | "system";

interface OrientationState {
  mode: OrientationMode;
  setMode: (mode: OrientationMode) => void;
}

const STORAGE_KEY = "healthpass-orientation";

/**
 * Applies the choice through the Screen Orientation API.
 *
 * Every branch is defensive. `screen.orientation.lock` is absent on desktop
 * Safari and on iOS altogether, and it rejects in a browser tab that is not an
 * installed app - a rejection there is the expected answer, not a fault, so it
 * is swallowed rather than surfaced. Where it does work, which is the installed
 * app and the Android wrapper, it is the whole mechanism.
 */
export function applyOrientation(mode: OrientationMode): void {
  if (typeof window === "undefined") return;

  const orientation = window.screen?.orientation as
    | (ScreenOrientation & {
        lock?: (o: string) => Promise<void>;
        unlock?: () => void;
      })
    | undefined;
  if (!orientation) return;

  try {
    if (mode === "portrait") {
      // portrait-primary, not portrait: the latter includes upside-down, which
      // is the flipping this setting exists to stop.
      void orientation.lock?.("portrait-primary")?.catch(() => undefined);
    } else {
      orientation.unlock?.();
    }
  } catch {
    // Not supported here. The browser decides, which is what "system" means.
  }
}

export const useOrientationStore = create<OrientationState>()(
  persist(
    (set) => ({
      mode: "portrait",
      setMode: (mode) => {
        applyOrientation(mode);
        set({ mode });
      },
    }),
    {
      name: STORAGE_KEY,
      onRehydrateStorage: () => (state) => {
        // Applied on rehydration as well as on change: the stored choice has to
        // take effect at startup, and it arrives after the first paint.
        if (state) applyOrientation(state.mode);
      },
    },
  ),
);
